import { getSessionToken } from './auth';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8787';

export type TierId = 'apprendista' | 'cavaliere' | 're';

export interface TierDef {
  id: TierId;
  name: string;
  price_cents: number;
  monthly_eur: number;
  perks: string[];
  weight?: number;
  monthly_votes?: number;
  monthly_quests?: number;
  monthly_veto?: number;
}

export interface DonorRow {
  amount_cents: number;
  message: string | null;
  paid_at: string;
  anonymous: number;
  display_name: string | null;
  yt_channel: string | null;
  avatar: string | null;
}

export interface DonationStats {
  donations: { donations_count: number; donors_count: number; total_cents: number };
  active_subscriptions: Array<{ tier: string; n: number }>;
}

function authHeaders(): HeadersInit {
  const t = getSessionToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function fetchTiers(): Promise<TierDef[]> {
  const r = await fetch(`${API_BASE}/api/donations/tiers`);
  const d = await r.json() as { tiers: TierDef[] };
  return d.tiers;
}

export async function fetchDonors(limit = 50): Promise<DonorRow[]> {
  const r = await fetch(`${API_BASE}/api/donations/list?limit=${limit}`);
  const d = await r.json() as { donors: DonorRow[] };
  return d.donors;
}

export async function fetchDonationStats(): Promise<DonationStats> {
  const r = await fetch(`${API_BASE}/api/donations/stats`);
  return r.json();
}

export async function createDonation(opts: {
  amount_cents: number;
  anonymous?: boolean;
  message?: string;
  locale?: string;
}): Promise<{ checkout_url: string }> {
  const r = await fetch(`${API_BASE}/api/donations/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      ...opts,
      return_url: window.location.origin + window.location.pathname,
    }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error((e as any).error ?? 'donation_failed');
  }
  return r.json();
}

export async function subscribeTier(tier: TierId, locale = 'it'): Promise<{ checkout_url: string }> {
  const r = await fetch(`${API_BASE}/api/donations/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      tier, locale,
      return_url: window.location.origin + window.location.pathname,
    }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    if ((e as any).error === 'login_required') throw new Error('login_required');
    throw new Error((e as any).error ?? 'subscribe_failed');
  }
  return r.json();
}

export async function fetchMyDonations() {
  const r = await fetch(`${API_BASE}/api/donations/me`, { headers: authHeaders() });
  if (!r.ok) return null;
  return r.json();
}

export async function cancelMySubscription(): Promise<void> {
  const r = await fetch(`${API_BASE}/api/donations/cancel`, { method: 'POST', headers: authHeaders() });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error((e as any).error ?? 'cancel_failed');
  }
}
